import { useState, useRef, useCallback } from "react";
import { rgbToHex, cmykString, dominantColors, clientToPixel, loupeRects, LOUPE_RADIUS, LOUPE_ZOOM, type PaletteColor } from "./colorPickerUtils";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer, FileDropZone } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";

function rgbToHsl(r: number, g: number, b: number): string {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
}

const TOOL_INFO_CONTENT = {
  what: `Lets you click anywhere on an uploaded image to pick and copy the exact color at that point, useful for matching a color from a photo or existing design.`,
  how: `Upload an image. Move over it to see a magnifier (each pixel drawn 8 times larger, with its edges marked), click the color you want, and its hex, RGB, HSL and CMYK values are shown for copying. The most common colors in the image are listed as a palette under the picture; click one to see its values. With the picture focused, the arrow keys move the picked pixel one step (hold Shift for ten).`,
  tip: `A single pixel's color can shift subtly across a gradient, a photo or JPEG artifacts, so use the magnifier to land on the exact pixel. CMYK here is a simple conversion from RGB with no printer profile, so a print shop's own conversion may differ a little. The image and the palette stay in your browser.`,
};

export default function ColorPickerImage() {
  const { gateShare } = useShareGate();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ hex: string; rgb: string; hsl: string; cmyk: string; x?: number; y?: number } | null>(null);
  const [palette, setPalette] = useState<PaletteColor[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const loupeRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [loupe, setLoupe] = useState<{ left: number; top: number; hex: string } | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setPicked(null);
    setPalette([]);
  }, []);

  const describe = (r: number, g: number, b: number, x?: number, y?: number) =>
    ({ hex: rgbToHex(r, g, b), rgb: `rgb(${r}, ${g}, ${b})`, hsl: rgbToHsl(r, g, b), cmyk: cmykString(r, g, b), x, y });

  const pickAt = (x: number, y: number) => {
    const ctx = canvasRef.current?.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    setPicked(describe(pixel[0], pixel[1], pixel[2], x, y));
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx?.drawImage(img, 0, 0);
    try {
      const data = ctx?.getImageData(0, 0, canvas.width, canvas.height);
      setPalette(data ? dominantColors(data.data, data.width, data.height, 8) : []);
    } catch { setPalette([]); }
  };

  const pixelAt = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    // The canvas is drawn with object-contain, so the picture can sit inside a larger box with empty bars.
    return clientToPixel(canvas.getBoundingClientRect(), canvas.width, canvas.height, clientX, clientY);
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const p = pixelAt(e.clientX, e.clientY);
    if (p) pickAt(p.x, p.y);
  };

  const drawLoupe = (x: number, y: number) => {
    const canvas = canvasRef.current, lc = loupeRef.current;
    const lctx = lc?.getContext("2d");
    if (!canvas || !lc || !lctx) return;
    const r = loupeRects(x, y, LOUPE_RADIUS, canvas.width, canvas.height, LOUPE_ZOOM);
    lctx.imageSmoothingEnabled = false;
    lctx.fillStyle = "#888";
    lctx.fillRect(0, 0, r.size, r.size);
    if (r.sw > 0 && r.sh > 0) lctx.drawImage(canvas, r.sx, r.sy, r.sw, r.sh, r.dx, r.dy, r.dw, r.dh);
    // Pixel boundaries
    lctx.lineWidth = 1;
    lctx.strokeStyle = "rgba(0,0,0,0.28)";
    lctx.beginPath();
    for (let i = 0; i <= LOUPE_RADIUS * 2 + 1; i++) { const o = i * LOUPE_ZOOM + 0.5; lctx.moveTo(o, 0); lctx.lineTo(o, r.size); lctx.moveTo(0, o); lctx.lineTo(r.size, o); }
    lctx.stroke();
    // The pixel that a click will pick
    const c = LOUPE_RADIUS * LOUPE_ZOOM;
    lctx.strokeStyle = "#000"; lctx.lineWidth = 2; lctx.strokeRect(c + 1, c + 1, LOUPE_ZOOM - 2, LOUPE_ZOOM - 2);
    lctx.strokeStyle = "#fff"; lctx.lineWidth = 1; lctx.strokeRect(c - 0.5, c - 0.5, LOUPE_ZOOM + 1, LOUPE_ZOOM + 1);
    const px = canvas.getContext("2d", { willReadFrequently: true })!.getImageData(x, y, 1, 1).data;
    return rgbToHex(px[0], px[1], px[2]);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const p = pixelAt(e.clientX, e.clientY);
    const wrap = wrapRef.current;
    if (!p || !wrap) { setLoupe(null); return; }
    const hex = drawLoupe(p.x, p.y);
    const box = wrap.getBoundingClientRect();
    const size = (LOUPE_RADIUS * 2 + 1) * LOUPE_ZOOM;
    let left = e.clientX - box.left + 18, top = e.clientY - box.top + 18;
    if (left + size > box.width) left = e.clientX - box.left - size - 18; // flip to the other side near the right edge
    if (top + size + 20 > box.height) top = e.clientY - box.top - size - 38;
    setLoupe({ left: Math.max(0, left), top: Math.max(0, top), hex: hex ?? "" });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const d = e.shiftKey ? 10 : 1;
    const dx = e.key === "ArrowLeft" ? -d : e.key === "ArrowRight" ? d : 0;
    const dy = e.key === "ArrowUp" ? -d : e.key === "ArrowDown" ? d : 0;
    if (!dx && !dy && e.key !== "Enter") return;
    e.preventDefault();
    const x = Math.min(canvas.width - 1, Math.max(0, (picked?.x ?? Math.floor(canvas.width / 2)) + dx));
    const y = Math.min(canvas.height - 1, Math.max(0, (picked?.y ?? Math.floor(canvas.height / 2)) + dy));
    pickAt(x, y);
  };

  const copy = (text: string) => gateShare(async () => {
    try { await navigator.clipboard.writeText(text); toast.success(`Copied ${text}`); return true; }
    catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; }
  });

  return (
    <ToolContainer>
      {!imageUrl ? (
        <FileDropZone
          gateAtDownload
          accept="image/*"
          onFiles={handleFiles}
          label="Drop an image to pick colors from"
          description="Click anywhere on the image to sample that pixel's color"
        />
      ) : (
        <div className="space-y-4">
          <div ref={wrapRef} className="relative rounded-lg border overflow-hidden bg-[repeating-conic-gradient(#eee_0%_25%,transparent_0%_50%)] bg-[length:16px_16px]">
            <img src={imageUrl} onLoad={handleImageLoad} className="hidden" alt="" />
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              onPointerMove={handlePointerMove}
              onPointerLeave={() => setLoupe(null)}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role="img"
              aria-label="Your image. Click a point to pick its color, or use the arrow keys to move the picked pixel."
              className="w-full h-auto cursor-crosshair max-h-[500px] object-contain mx-auto block touch-none"
            />
            <div className="absolute pointer-events-none rounded border-2 border-white shadow-lg bg-black/80" style={{ left: loupe?.left ?? 0, top: loupe?.top ?? 0, display: loupe ? "block" : "none" }} aria-hidden="true">
              <canvas ref={loupeRef} width={(LOUPE_RADIUS * 2 + 1) * LOUPE_ZOOM} height={(LOUPE_RADIUS * 2 + 1) * LOUPE_ZOOM} className="block" />
              <p className="text-center text-[10px] font-mono text-white py-0.5">{loupe?.hex}</p>
            </div>
          </div>

          {palette.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Main colors in this image (click one to see its values)</p>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Main colors in this image">
                {palette.map(c => (
                  <button
                    key={c.hex}
                    type="button"
                    onClick={() => setPicked(describe(c.rgb[0], c.rgb[1], c.rgb[2]))}
                    aria-label={`Use palette color ${c.hex}, ${Math.max(1, Math.round(c.share * 100))} percent of the image`}
                    className="flex items-center gap-2 rounded-lg border bg-card px-2 py-1.5 text-xs hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="h-6 w-6 rounded border shrink-0" style={{ backgroundColor: c.hex }} />
                    <span className="font-mono">{c.hex}</span>
                    <span className="text-muted-foreground">{Math.max(1, Math.round(c.share * 100))}%</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {picked && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {([["HEX", picked.hex], ["RGB", picked.rgb], ["HSL", picked.hsl], ["CMYK", picked.cmyk]] as const).map(([label, value]) => (
                <div key={label} className="p-3 rounded-lg border bg-card flex items-center gap-3">
                  {label === "HEX" && <div className="w-8 h-8 rounded shrink-0 border" style={{ backgroundColor: picked.hex }} />}
                  <div className="min-w-0 flex-1"><p className="text-[10px] text-muted-foreground">{label}</p><p className="text-sm font-mono truncate">{value}</p></div>
                  <Button size="icon" variant="ghost" aria-label={`Copy ${label} value ${value}`} title={`Copy ${label}`} onClick={() => copy(value)}><Copy className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}

          <Button variant="outline" onClick={() => { setImageUrl(null); setPicked(null); setPalette([]); setLoupe(null); }}>Upload Different Image</Button>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
